/**
 * Editor API — Batch save queue + session auth
 * Queues translation edits and flushes them in a single batch API call.
 * Uses editor session JWTs (es_ prefixed) for authentication with auto-renewal.
 */

import type { CapturedSnapshot, SnapshotTextEntry } from './snapshot-capture.js';

export interface QueuedTranslation {
  key: string;
  locale: string;
  namespace: string;
  text: string;
  styles: Record<string, string[]>;
}

// ============================================
// Snapshot API types
// ============================================

export interface SnapshotListItem {
  snapshotUuid: string;
  pageUrl: string;
  title: string;
  capturedBy: string;
  createdAt: string;
  expiresAt: string;
  textEntryCount: number;
  status: string;
}

export interface SnapshotDetail {
  snapshotUuid: string;
  pageUrl: string;
  title: string;
  html: string;
  textEntries: SnapshotTextEntry[];
  viewport: { width: number; height: number; devicePixelRatio: number };
  createdAt: string;
  expiresAt: string;
}

export interface TargetStatus {
  text: string;
  rowState: string;        // "empty" | "machine_translated" | "edited"
  isConfirmed: boolean;
  /** APPROVED for delivery — NOT the same as actually served. This is the label
   *  AND the action of the PM's Mark/Unmark toggle, so it must keep meaning
   *  approval. For "would GET /translations serve this?", read willDeliver. */
  isReady: boolean;
  /** Whether GET /translations would ACTUALLY serve this key right now.
   *  Server-derived from the delivery axes (approved-or-locked, non-empty
   *  target, translatable source, live target) — a sibling fact to isReady,
   *  not a restatement: an APPROVED row with an EMPTY target is approved and
   *  still not delivered. Optional for backward compatibility — a backend
   *  predating the field omits it, leaving this undefined (fetchStatus returns
   *  the parsed body unmodified, so absence survives as undefined rather than
   *  false). Callers must fall back to isReady, not to false. */
  willDeliver?: boolean;
  isLocked: boolean;       // workflow locked OR delivery locked
  /** Whether the calling editor session can confirm/reject this row.
   *  Server-computed via can_user_edit_row() — reflects PM/admin override,
   *  postreview, and per-stage assignment. False for rows past the user's
   *  stage (e.g. translator looking at a row they've already pushed forward).
   *  Optional for backward compatibility with older backend versions. */
  canEdit?: boolean;
  /** Current workflow stage index. Optional for backward compatibility. */
  stageIndex?: number;
}

/** One stage in the project's workflow. */
export interface WorkflowStageInfo {
  index: number;
  name: string;
  role: string | null;
  /** True for the Postreview stage — rows at/past it cannot Advance. */
  isPostreview: boolean;
}

/** Workflow context for the calling editor session. Cached per locale with a
 *  TTL (see fetchWorkflowInfo) — the workflow can change mid-session and can
 *  differ per locale via a per-target override. */
export interface EditorWorkflowInfo {
  stages: WorkflowStageInfo[];
  /** Stage indices the user is assigned to via UserTargetLink. Empty for PM/admin. */
  userAssignedStages: number[];
  isPmAdmin: boolean;
  workflowMode: 'per_row' | 'per_file';
  /** Number of non-Postreview (work) stages — the server's advance boundary:
   *  a row with stageIndex >= this is in postreview and cannot Advance.
   *  0 also covers a project with no workflow configured at all. */
  workStageCount: number;
}

/** Whether a row sits at/past the workflow's postreview boundary — the same
 *  check the server's advance endpoint enforces ("Row is already past the
 *  final stage"). False when workflow info or the row's stage is unknown:
 *  the sidebar then keeps its old behavior and lets the server decide. */
export function isRowAtPostreview(
  info: EditorWorkflowInfo | null,
  stageIndex: number | undefined,
): boolean {
  return info !== null && typeof stageIndex === 'number' && stageIndex >= info.workStageCount;
}

/** Per-key outcome for a batch item the server refused to write (per-row
 *  authorization). The batch analogue of the dashboard save's 423 "locked" /
 *  403 "forbidden" rejection with a row snapshot. */
export interface DeniedRow {
  key: string;
  /** "locked" (row locked, PM-only), "forbidden" (not assigned to this row/stage),
   *  or "busy" (target's MT fill in flight — transient, the edit is retried). */
  reason: 'locked' | 'forbidden' | 'busy' | string;
  /** Server's current translation text (null if the row doesn't exist yet). */
  current_text: string | null;
}

export type StatusCallback = (status: 'unsaved' | 'saving' | 'saved' | 'error', message?: string) => void;

export class EditorAPI {
  private queue: QueuedTranslation[] = [];
  private apiBase: string;
  private sessionToken: string;
  private projectId: string;
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private renewalInterval: ReturnType<typeof setInterval> | null = null;
  private onStatus: StatusCallback = () => {};
  private onFlushed: (keys: string[]) => void = () => {};
  private onSessionExpired: (() => void) | null = null;
  // Workflow context cache — keyed by locale (a per-target override can give
  // one locale a different workflow) and TTL-bound (the PM can change the
  // project's workflow from the dashboard mid-session; a boundary pinned at
  // first fetch would render buttons the server refuses). Promises are
  // cached so concurrent callers share one in-flight fetch.
  private workflowInfoCache = new Map<
    string,
    { promise: Promise<EditorWorkflowInfo | null>; fetchedAt: number }
  >();
  private static readonly WORKFLOW_INFO_TTL_MS = 5 * 60 * 1000;
  /** Whether this session may lazily create rows for keys with no target row
   *  yet. Mirrors the server's create gate (creation is stage-0 work — PM/admin
   *  or stage-0 assignees); refreshed from targets/status `can_create`.
   *  Default true: older backends don't send it and don't gate creates. */
  canCreateMissing = true;

  /** True while this locale's target has an MT fill queued/running (refreshed
   *  from targets/status `target_busy`). canEdit stays the permission truth
   *  during the window; stage transitions would 423 until it clears, and
   *  saves are denied as "busy" and auto-retried by the flush loop. */
  targetBusy = false;

  constructor(apiBase: string, sessionToken: string, projectId: string) {
    this.apiBase = apiBase.replace(/\/$/, '');
    this.sessionToken = sessionToken;
    this.projectId = projectId;
  }

  setStatusCallback(cb: StatusCallback): void {
    this.onStatus = cb;
  }

  setFlushedCallback(cb: (keys: string[]) => void): void {
    this.onFlushed = cb;
  }

  setSessionExpiredCallback(cb: () => void): void {
    this.onSessionExpired = cb;
  }

  /**
   * Start auto-flush (30s interval + visibilitychange) and session renewal (60s check)
   */
  startAutoFlush(): void {
    this.flushInterval = setInterval(() => this.flush(), 30_000);
    document.addEventListener('visibilitychange', this.handleVisibility);

    // Check session renewal every 60s
    this.renewalInterval = setInterval(() => this.maybeRenewSession(), 60_000);
  }

  /**
   * Stop auto-flush and flush remaining items
   */
  async stopAndFlush(): Promise<void> {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    if (this.renewalInterval) {
      clearInterval(this.renewalInterval);
      this.renewalInterval = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibility);
    await this.flush();
  }

  /**
   * Queue a translation for saving
   */
  queueSave(item: QueuedTranslation): void {
    // Replace existing entry for same key+locale
    const idx = this.queue.findIndex(
      (q) => q.key === item.key && q.locale === item.locale && q.namespace === item.namespace
    );
    if (idx >= 0) {
      this.queue[idx] = item;
    } else {
      this.queue.push(item);
    }
    this.onStatus('unsaved', `${this.queue.length} unsaved`);

    // Auto-flush if queue is large
    if (this.queue.length > 50) {
      this.flush();
    }
  }

  /**
   * Flush all queued translations to the server
   */
  async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const items = [...this.queue];
    this.onStatus('saving');

    try {
      const response = await this.authFetch(
        `${this.apiBase}/api/integrations/targets/batch`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            translations: items.map((t) => ({
              key: `${t.locale}:${t.namespace}:${t.key}`,
              value: { text: t.text, styles: t.styles },
            })),
          }),
        }
      );

      if (response.status === 401) {
        this.handleSessionExpired();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Per-key authorization outcomes (dashboard parity): the server writes
      // what it can and reports refused rows in denied_rows instead of failing
      // the batch. A 200 with denied rows must NOT read as "all saved".
      const body = (await response.json().catch(() => null)) as {
        denied?: number;
        denied_rows?: DeniedRow[];
      } | null;
      const deniedRows = body?.denied_rows ?? [];
      // "busy" (TARGET_BUSY: the target's MT fill is in flight) is transient —
      // keep those edits queued and retry after the fill's sweep window,
      // instead of reverting the draft like a real authorization denial.
      const busyKeys = new Set(
        deniedRows.filter((d) => d.reason === 'busy').map((d) => d.key)
      );
      const hardDenied = deniedRows.filter((d) => d.reason !== 'busy');

      // Remove flushed items from queue (new items may have been added during
      // fetch) — except busy ones, which stay queued for the retry flush.
      const retained: QueuedTranslation[] = [];
      for (const item of items) {
        const idx = this.queue.indexOf(item);
        if (busyKeys.has(`${item.locale}:${item.namespace}:${item.key}`)) {
          retained.push(item);
          continue;
        }
        if (idx >= 0) this.queue.splice(idx, 1);
      }

      // Notify sidebar to re-fetch status for flushed keys — for denied rows
      // the refetch restores the server's current text in place of the
      // refused edit (the batch analogue of the dashboard's 423/403 snapshot).
      // Busy rows are excluded: their draft is still pending, not refused.
      this.onFlushed(
        items
          .filter((i) => !busyKeys.has(`${i.locale}:${i.namespace}:${i.key}`))
          .map((i) => i.key)
      );

      if (retained.length > 0) {
        this.scheduleBusyRetry();
      }

      if (hardDenied.length > 0) {
        const lockedCount = hardDenied.filter((d) => d.reason === 'locked').length;
        const message =
          hardDenied.length === 1
            ? lockedCount === 1
              ? '1 change not saved — row is locked (only a PM can edit it)'
              : '1 change not saved — you are not assigned to that row'
            : `${hardDenied.length} changes not saved — locked or not assigned to you`;
        this.onStatus('error', message);
      } else if (retained.length > 0) {
        this.onStatus(
          'unsaved',
          `${retained.length} waiting — machine translation is filling this page, retrying shortly`
        );
      } else if (this.queue.length === 0) {
        this.onStatus('saved', 'All changes saved');
      } else {
        this.onStatus('unsaved', `${this.queue.length} unsaved`);
      }
    } catch (err) {
      this.onStatus('error', `Save failed: ${(err as Error).message}`);
    }
  }

  private busyRetryTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Re-flush edits refused with reason "busy" (target MT fill in flight).
   * One pipeline sweep (~30 s worst case) clears the fill; a single pending
   * timer is enough — each retry re-schedules itself while busy rows remain.
   */
  private scheduleBusyRetry(): void {
    if (this.busyRetryTimer) return;
    this.busyRetryTimer = setTimeout(() => {
      this.busyRetryTimer = null;
      this.flush();
    }, 8000);
  }

  private statusAbort: AbortController | null = null;

  /**
   * Fetch translation status for a batch of keys (includes unconfirmed rows).
   * Cancels any in-flight status request. Caps at 200 keys per request.
   */
  async fetchStatus(
    keys: string[],
    locale: string,
    namespace: string
  ): Promise<Record<string, TargetStatus | null>> {
    if (keys.length === 0) return {};

    // Cancel previous in-flight request
    if (this.statusAbort) this.statusAbort.abort();
    this.statusAbort = new AbortController();

    // Cap keys to avoid exceeding URL length limits
    const batch = keys.slice(0, 200);

    const params = new URLSearchParams({ locale, namespace, keys: batch.join(',') });
    try {
      const response = await this.authFetch(
        `${this.apiBase}/api/integrations/targets/status?${params}`,
        { signal: this.statusAbort.signal }
      );
      if (response.status === 401) {
        this.handleSessionExpired();
        return {};
      }
      if (!response.ok) return {};
      const data = await response.json();
      // Server sends can_create when the caller is an editor session — false
      // means keys WITHOUT a row must render read-only (the batch save would
      // refuse the lazy create). Absent (older backend) = allowed.
      this.canCreateMissing = data.can_create !== false;
      // Absent (older backend) = not busy — old servers bake the fill gate
      // into canEdit instead, so nothing extra to surface.
      this.targetBusy = data.target_busy === true;
      return data.translations;
    } catch (err) {
      if ((err as Error).name === 'AbortError') return {};
      return {};
    }
  }

  get pendingCount(): number {
    return this.queue.length;
  }

  /**
   * Fetch workflow context (stages, user's stage assignments, PM/admin flag,
   * advance boundary) for the EFFECTIVE workflow of `locale` — the server
   * resolves the per-target override (`target ?? project`) when a locale is
   * given. Cached per locale with a 5-min TTL so a mid-session workflow
   * change from the dashboard settings is picked up without a page reload.
   * Returns null if the fetch fails; callers should hide the strip in that case.
   */
  async fetchWorkflowInfo(locale?: string): Promise<EditorWorkflowInfo | null> {
    const cacheKey = locale ?? '';
    const cached = this.workflowInfoCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < EditorAPI.WORKFLOW_INFO_TTL_MS) {
      return cached.promise;
    }
    const promise = (async (): Promise<EditorWorkflowInfo | null> => {
      try {
        const url =
          `${this.apiBase}/api/integrations/editor/workflow` +
          (locale ? `?locale=${encodeURIComponent(locale)}` : '');
        const response = await this.authFetch(url);
        if (response.status === 401) {
          this.handleSessionExpired();
          return null;
        }
        if (!response.ok) return null;
        const data = await response.json();
        const stages: WorkflowStageInfo[] = (Array.isArray(data.stages) ? data.stages : []).map(
          (s: { index: number; name: string; role: string | null; is_postreview?: boolean }) => ({
            index: s.index,
            name: s.name,
            role: s.role ?? null,
            isPostreview: Boolean(s.is_postreview),
          })
        );
        return {
          stages,
          userAssignedStages: Array.isArray(data.user_assigned_stages) ? data.user_assigned_stages : [],
          isPmAdmin: Boolean(data.is_pm_admin),
          workflowMode: data.workflow_mode === 'per_file' ? 'per_file' : 'per_row',
          // Older backends don't send work_stage_count; before Postreview
          // stages existed the boundary was len(stages), so fall back to that.
          workStageCount:
            typeof data.work_stage_count === 'number' ? data.work_stage_count : stages.length,
        };
      } catch {
        return null;
      }
    })();
    this.workflowInfoCache.set(cacheKey, { promise, fetchedAt: Date.now() });
    return promise;
  }

  // ============================================
  // Session renewal
  // ============================================

  /**
   * Check if session is within 5min of expiry, and renew if so.
   *
   * The threshold is intentionally generous because background tabs throttle
   * setInterval to once-per-minute (Chrome) or even less aggressively, and
   * a sleeping laptop pauses timers entirely. Renewing early gives us
   * headroom for those cases — the renewal interval still runs before the
   * 15min server-side TTL elapses even if it fires a minute or two late.
   */
  private async maybeRenewSession(): Promise<void> {
    const exp = this.getSessionExp();
    if (!exp) return;

    const timeLeft = exp * 1000 - Date.now();
    if (timeLeft > 300_000) return; // More than 5 min left, skip

    await this.renewSession();
  }

  /**
   * Attempt one renewal and signal whether the token actually changed.
   * Used by the 401-retry path in API calls.
   */
  private async tryRenew(): Promise<boolean> {
    const before = this.sessionToken;
    await this.renewSession();
    return this.sessionToken !== before;
  }

  private async renewSession(): Promise<void> {
    try {
      const response = await fetch(`${this.apiBase}/api/editor/renew`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.sessionToken}`,
        },
      });

      if (response.status === 401) {
        this.handleSessionExpired();
        return;
      }

      if (!response.ok) return;

      const data = await response.json();
      this.sessionToken = data.session_token;

      // Update sessionStorage + preview cookie. The cookie tells the edge
      // proxy this is an active editor session so it should fetch drafts.
      try {
        const raw = this.sessionToken.startsWith('es_') ? this.sessionToken.slice(3) : this.sessionToken;
        const parts = raw.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          sessionStorage.setItem('__lr_editor_session', JSON.stringify({
            token: this.sessionToken,
            exp: payload.exp || 0,
          }));
          document.cookie = '__lr_editor_active=1; path=/; SameSite=Lax';
        }
      } catch { /* non-critical */ }
    } catch {
      // Network error — will retry on next interval
    }
  }

  private getSessionExp(): number | null {
    try {
      const raw = this.sessionToken.startsWith('es_') ? this.sessionToken.slice(3) : this.sessionToken;
      const parts = raw.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload.exp || null;
    } catch {
      return null;
    }
  }

  /**
   * Authenticated fetch with one-shot 401 retry. If the request comes back
   * 401, we attempt a session renewal and replay the request once with the
   * new token. If renewal fails (token past the server's 2min grace), the
   * caller still gets a 401 and falls through to handleSessionExpired.
   *
   * Always passes `init.signal` straight through so abortable callers
   * (e.g. fetchStatus) still cancel correctly.
   */
  private async authFetch(url: string, init: RequestInit = {}): Promise<Response> {
    const buildHeaders = (): HeadersInit => {
      const h = new Headers(init.headers);
      h.set('Authorization', `Bearer ${this.sessionToken}`);
      return h;
    };

    let response = await fetch(url, { ...init, headers: buildHeaders() });
    if (response.status === 401) {
      const renewed = await this.tryRenew();
      if (renewed) {
        response = await fetch(url, { ...init, headers: buildHeaders() });
      }
    }
    return response;
  }

  private handleSessionExpired(): void {
    // Clear the preview cookie too so the edge stops serving drafts as
    // soon as the session is gone, even if the page stays open.
    document.cookie = '__lr_editor_active=; path=/; SameSite=Lax; Max-Age=0';
    this.onStatus('error', 'Session expired. Please reopen the editor from the dashboard.');
    if (this.onSessionExpired) {
      this.onSessionExpired();
    }
  }

  private handleVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      this.flush();
    } else if (document.visibilityState === 'visible') {
      // Tab returned to foreground. Background timers throttle/pause, so
      // by the time the user comes back the session may be close to (or
      // past) expiry. Try a renewal immediately — if still within the
      // 2min server grace, this saves the next API call from 401.
      this.maybeRenewSession();
    }
  };

  // ============================================
  // Snapshot API
  // ============================================

  /**
   * Upload a captured snapshot. Returns the created snapshot's UUID so the
   * caller can build a share URL (`?__lr_snapshot={uuid}`).
   */
  async createSnapshot(
    snapshot: CapturedSnapshot,
  ): Promise<{ snapshotUuid: string } | null> {
    try {
      const response = await fetch(`${this.apiBase}/api/editor/snapshots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.sessionToken}`,
        },
        body: JSON.stringify({
          pageUrl: snapshot.pageUrl,
          title: snapshot.title,
          viewport: snapshot.viewport,
          textEntries: snapshot.textEntries,
          html: snapshot.html,
        }),
      });

      if (response.status === 401) {
        this.handleSessionExpired();
        return null;
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return { snapshotUuid: data.snapshot_uuid || data.snapshotUuid };
    } catch (err) {
      this.onStatus('error', `Snapshot failed: ${(err as Error).message}`);
      return null;
    }
  }

  /**
   * Re-capture an existing snapshot. Server appends a new version and keeps
   * the same `snapshotUuid`. Returns the new version number on success.
   */
  async recaptureSnapshot(
    snapshotUuid: string,
    snapshot: CapturedSnapshot,
  ): Promise<{ snapshotUuid: string; version: number } | null> {
    try {
      const response = await fetch(
        `${this.apiBase}/api/editor/snapshots/${encodeURIComponent(snapshotUuid)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.sessionToken}`,
          },
          body: JSON.stringify({
            pageUrl: snapshot.pageUrl,
            title: snapshot.title,
            viewport: snapshot.viewport,
            textEntries: snapshot.textEntries,
            html: snapshot.html,
          }),
        },
      );

      if (response.status === 401) {
        this.handleSessionExpired();
        return null;
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return {
        snapshotUuid: data.snapshot_uuid || data.snapshotUuid,
        version: data.version || 1,
      };
    } catch (err) {
      this.onStatus('error', `Re-capture failed: ${(err as Error).message}`);
      return null;
    }
  }

  /**
   * Fetch a single snapshot by UUID. Returns null on auth failure or 404.
   */
  async getSnapshot(snapshotUuid: string): Promise<SnapshotDetail | null> {
    try {
      const response = await fetch(
        `${this.apiBase}/api/editor/snapshots/${encodeURIComponent(snapshotUuid)}`,
        {
          headers: { 'Authorization': `Bearer ${this.sessionToken}` },
        },
      );
      if (response.status === 401) {
        this.handleSessionExpired();
        return null;
      }
      if (!response.ok) return null;
      return (await response.json()) as SnapshotDetail;
    } catch {
      return null;
    }
  }

  /**
   * List all snapshots the current editor session can see.
   */
  async listSnapshots(): Promise<SnapshotListItem[]> {
    try {
      const response = await fetch(`${this.apiBase}/api/editor/snapshots`, {
        headers: { 'Authorization': `Bearer ${this.sessionToken}` },
      });
      if (response.status === 401) {
        this.handleSessionExpired();
        return [];
      }
      if (!response.ok) return [];
      const data = await response.json();
      return data.snapshots || [];
    } catch {
      return [];
    }
  }

  /**
   * Delete (soft) a snapshot by UUID.
   */
  async deleteSnapshot(snapshotUuid: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.apiBase}/api/editor/snapshots/${encodeURIComponent(snapshotUuid)}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${this.sessionToken}` },
        },
      );
      if (response.status === 401) {
        this.handleSessionExpired();
        return false;
      }
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Workflow transitions (Phase 3). Per-key endpoints on the integrations API
   * that mirror the dashboard's row-level transitions but are addressable by
   * composite editor key (locale:namespace:auto_xxx).
   *
   * Returns the parsed JSON on success, or null on failure (caller usually
   * shows a toast / re-fetches status).
   */
  async transitionTarget(
    composite: string,
    action: 'confirm' | 'unconfirm' | 'reject' | 'advance' | 'mark-ready' | 'unmark-ready',
  ): Promise<Record<string, unknown> | null> {
    try {
      const response = await this.authFetch(
        `${this.apiBase}/api/integrations/targets/${encodeURIComponent(composite)}/${action}`,
        { method: 'POST' },
      );
      if (response.status === 401) {
        this.handleSessionExpired();
        return null;
      }
      if (!response.ok) {
        // 423 TARGET_BUSY: the target's MT fill is in flight — transient
        // (clears within one pipeline sweep), not a permissions failure.
        const errBody = (await response.json().catch(() => null)) as {
          error?: { code?: string };
        } | null;
        if (response.status === 423 && errBody?.error?.code === 'TARGET_BUSY') {
          this.onStatus(
            'error',
            'Machine translation is filling this page — try again in a few seconds.'
          );
        } else {
          this.onStatus('error', `${action} failed: HTTP ${response.status}`);
        }
        return null;
      }
      return (await response.json()) as Record<string, unknown>;
    } catch (err) {
      this.onStatus('error', `${action} failed: ${(err as Error).message}`);
      return null;
    }
  }
}
